import React, { useContext, useMemo } from 'react';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { translate } from '../../../../../../types/translation/Translator';
import { ActionModal } from '../../../../../components/Modal';
import { Order, OrderInput } from '../../../../../../types/order';
import CustomFieldInput from '../../../../Common/CustomFieldInput';
import { CustomField, CustomFieldType, CustomFieldValue } from '../../../../../../types/custom_field';
import { WithCustomFields } from '../../../../../../types/custom_field/customField';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { OrderFlowContext } from '../../../../../../context/OrderFlowContext';
import { Button } from '../../../../../components/Button';
import { useRequest } from '../../../../../../requests/common/requestHook';
import { OrderRequest } from '../../../../../../requests/order.request';
import { OrderContext } from '../../../../../../context/OrderContext';
import { GXO } from '../../../../../../types/customer/GXO/GXO';
import { FlowType } from '../../../../../../types/order/orderFlow';

interface GXOCustomFieldModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;

  order: OrderInput;
  setOrder: (order: OrderInput) => void;

  actionId: string;
}

export default function GXOCustomFieldModal({ open, setOpen, order, setOrder, actionId }: GXOCustomFieldModalProps) {
  const { customFields } = useContext(CustomFieldContext);
  const { orderFlows } = useContext(OrderFlowContext);
  const { orders, setOrders } = useContext(OrderContext);

  const flow = orderFlows.get(order.flowId);
  if (!flow) return null;

  const inboundIncluded =
    actionId === GXO.arrivedOrderStatusId
      ? new Set([
          GXO.supplierCustomFieldId,
          GXO.purchaseOrderCustomFieldId,
          GXO.expectedDeliveryDateCustomFieldId,
          GXO.carrierCustomFieldId,
          GXO.trailerNoCustomFieldId,
          GXO.truckNumberCustomFieldId,
          GXO.packingListNumberCustomFieldId,
          GXO.totalPalletsCustomFieldId,
          GXO.totalCartonsCustomFieldId,
        ])
      : new Set([GXO.documentationCustomFieldId]);

  const outboundIncluded =
    actionId === GXO.confirmCollectionDateOrderStatusId ? new Set([GXO.collectionDateCustomFieldId]) : new Set([]);

  const included: Set<string> = order.type === FlowType.inbound ? inboundIncluded : outboundIncluded;

  const items = useMemo(() => {
    return [...customFields.values()]
      .filter(v => flow.customFieldIds.includes(v.id) && included.has(v.id))
      .sort((a, b) => b.index - a.index || a.name.localeCompare(b.name));
  }, [customFields, order, included]);

  const [update, { loading }] = useRequest(OrderRequest.update);
  const [process, { loading: processLoading }] = useRequest(OrderRequest.process);

  const handleUpdate = async () => {
    const result = await update({ data: [order.forUpdate()] });
    if (!result) return;

    const response = await process({ id: order.id, statusId: actionId });
    if (!response) return;

    orders.set(response.id, new Order(response));
    setOrders(new Map(orders));

    orders.set(result[0].id, new Order(result[0]));
    setOrders(new Map(orders));

    setOpen(false);
  };

  const disabled = Array.from(included).every((key: any) => order.customFields.get(key)?.value);

  const footer = () => {
    return (
      <div className='flex justify-end'>
        <Button
          disabled={!disabled}
          loading={loading || processLoading}
          text={translate({ key: TranslationKey.update })}
          onClick={handleUpdate}
        />
      </div>
    );
  };

  const customFieldText = (field: CustomField, value?: string) => {
    if (!value) return '';

    if (field.type === CustomFieldType.date) return value;

    return value || '';
  };

  const content = (item: CustomField, cf?: CustomFieldValue) => {
    return (
      <div>
        <CustomFieldInput
          disabled={false}
          value={customFieldText(item, cf?.value)}
          entity={order}
          item={item}
          onChange={setOrder}
          name={cf?.name}
          generated={cf?.generated}
          index={cf?.index}
          id={cf?.id}
          users={new Map()}
          contacts={new Map()}
          productMasterData={new Map()}
        />
      </div>
    );
  };

  const customValues = order.customFieldValues();
  const toFilter = new Set(
    order
      .customFieldValues()
      .filter(item => item.id.includes('::'))
      .map(item => item.id.split('::')[0]),
  );

  const children = useMemo(() => {
    return [
      ...items.map(item => {
        if (toFilter.has(item.id)) return null;
        if (!item.onCreate && !order.id) return null;

        const cf = WithCustomFields.toCustomFieldsMap(order).get(item.id);
        return content(item, cf);
      }),

      ...customValues.map(cf => {
        if (!cf.id.includes('::')) return null;

        const [id, suffix] = cf.id.split('::');
        if (!id) return null;

        const item = customFields.get(id);
        if (!item) return null;

        return content(item, cf);
      }),
    ];
  }, [items, customValues, customFields, order]);

  if (!children.length) return null;

  return (
    <ActionModal
      footer={footer()}
      title={translate({ key: TranslationKey.update })}
      open={open}
      setOpen={setOpen}
      className='max-w-[920px]'
    >
      <div className='flex flex-col gap-2'>{children}</div>
    </ActionModal>
  );
}
